import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useI18n } from '@udemy-v2/i18n';
import { BlockList } from '@udemy-v2/react-core-components';
import { Icon } from '@udemy-v2/icons';
import { TextSkeleton } from '@udemy-v2/react-reveal-components';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import { AsyncStaticPriceText, useCoursePrice } from '@udemy-v2/react-course-pricing';
import { CollapsiblePurchaseSection } from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import { PRICE_EVENTING_BUYABLE_TYPES, PriceEventTrackingProvider, PURCHASE_PRICE_TYPES } from '@udemy-v2/react-currency-formatting';
import { AddToCartButton, WishlistButton } from '@udemy-v2/react-shopping-buttons';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { ClpPrimaryCtaButton } from '@/features/course-landing-page/components/clp-primary-cta-button/clp-primary-cta-button.react-component';
import { ClpCartSuccessModal } from '@/features/course-landing-page/components/clp-cart-success-modal';
import styles from './buy-box.module.scss';
import { CLPPurchaseOption, useClpPurchaseContext, useClpSelectedOption } from '../../context/clp-purchase-context.react-component';
import { useFeatures } from '@udemy-v2/react-experimentation';
interface CollapsibleBuyBoxProps {
  showRadio?: boolean;
}
const CollapsibleBuyBoxInner = t0 => {
  const $ = _c(62);
  const {
    showRadio
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["cart_success_desktop", "remove_buy_now_for_mweb_devices"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t1);
  let t2;
  if ($[1] !== featureVariantAssignments) {
    t2 = featureVariantAssignments.get("cart_success_desktop")?.configuration;
    $[1] = featureVariantAssignments;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const cartSuccessDesktopFeatureAssignment = t2;
  let t3;
  if ($[3] !== featureVariantAssignments) {
    t3 = featureVariantAssignments.get("remove_buy_now_for_mweb_devices")?.configuration;
    $[3] = featureVariantAssignments;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const removeBuyNowForMwebDevicesFeatureAssignment = t3;
  const isCartSuccessDesktopEnabled = cartSuccessDesktopFeatureAssignment?.skipAddToCartSuccessModal ?? false;
  const removeBuyNow = removeBuyNowForMwebDevicesFeatureAssignment?.removeBuyNow ?? false;
  const {
    gettext
  } = useI18n();
  const course = useClpCourse();
  const {
    isReloadingPrice,
    removeSubscriptionOptionEnabled,
    isConsumerSubscriptionPaused
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption,
    setSelectedPurchaseOption
  } = useClpSelectedOption();
  const coursePrice = useCoursePrice(Number(course.id));
  const isMobile = useMatchMedia("sm-max");
  const forceRedirectToCart = !!isMobile || isCartSuccessDesktopEnabled;
  const isPersonalPlanPrimaryPurchaseOptions = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;
  let t4;
  if ($[5] !== setSelectedPurchaseOption) {
    t4 = () => setSelectedPurchaseOption(CLPPurchaseOption.TRANSACTIONAL);
    $[5] = setSelectedPurchaseOption;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleExpand = t4;
  let t5;
  if ($[7] !== course.frontendTrackingId || $[8] !== course.id || $[9] !== coursePrice || $[10] !== gettext || $[11] !== isReloadingPrice) {
    const {
      discountPrice,
      listPrice,
      price
    } = coursePrice || {};
    const isPriceLoaded = coursePrice?.status === "loaded" && !isReloadingPrice;
    const showDiscountPrice = isPriceLoaded && discountPrice && listPrice && discountPrice.amount !== listPrice.amount;
    t5 = isReloadingPrice ? <><span className={classNames("ud-text-md")}>{gettext("Buy individual course")}</span><TextSkeleton lineCountPerParagraph={0} className={styles["price-skeleton"]} withTitle={true} /></> : isPriceLoaded ? <PriceEventTrackingProvider priceEventTrackingContext={{
      priceType: PURCHASE_PRICE_TYPES.individual_buyable,
      buyableType: PRICE_EVENTING_BUYABLE_TYPES.COURSE,
      buyableId: Number(course.id),
      buyableTrackingId: course.frontendTrackingId
    }}><span className={classNames("ud-text-md")}>{gettext("Buy individual course")}</span><AsyncStaticPriceText showDiscountDuration={true} courseId={Number(course.id)} listPriceString={listPrice?.formattedPrice} discountPriceString={price?.formattedPrice} discountPriceClassName={classNames("ud-heading-xl", styles["discount-price"])} discountDeadlineTextClassName={classNames(styles["discount-deadline"])} percentDiscount={coursePrice?.discountPercentForDisplay} percentDiscountClassName={classNames("ud-text-md", styles["percent-discount"])} listPriceClassName={classNames(showDiscountPrice ? "ud-text-md" : "ud-heading-xl", styles["list-price"])} showPercentDiscount={showDiscountPrice} className={classNames({
        [styles["price-text--with-discount"]]: showDiscountPrice
      })} useCompactDiscountDeadline={true} /></PriceEventTrackingProvider> : null;
    $[7] = course.frontendTrackingId;
    $[8] = course.id;
    $[9] = coursePrice;
    $[10] = gettext;
    $[11] = isReloadingPrice;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <>{t5}</>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const title = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon label="" size="xsmall" glyph="deal" />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== gettext) {
    t8 = gettext("30-day money-back guarantee");
    $[16] = gettext;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <BlockList.Item icon={t7}><span className="ud-text-sm">{t8}</span></BlockList.Item>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon label="" size="xsmall" glyph="lifetime" />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== gettext) {
    t11 = gettext("Full lifetime access");
    $[21] = gettext;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <BlockList.Item icon={t10}><span className="ud-text-sm">{t11}</span></BlockList.Item>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t9) {
    t13 = <BlockList padding="tight" size="small">{t9}{t12}</BlockList>;
    $[25] = t12;
    $[26] = t9;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = classNames(styles["buttons-container"], styles["buttons-container-collapsible"]);
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = Number(course.id);
  let t16;
  if ($[29] !== course.frontendTrackingId || $[30] !== t15) {
    t16 = [{
      id: t15,
      buyableObjectType: "course",
      frontendTrackingId: course.frontendTrackingId
    }];
    $[29] = course.frontendTrackingId;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      size: "large",
      udStyle: "primary"
    };
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== course || $[34] !== isMobile) {
    t18 = !isMobile ? <ClpCartSuccessModal course={course} /> : undefined;
    $[33] = course;
    $[34] = isMobile;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = !!forceRedirectToCart;
  let t20;
  if ($[36] !== t16 || $[37] !== t18 || $[38] !== t19) {
    t20 = <AddToCartButton buyables={t16} buttonStyleProps={t17} addToCartSuccessModal={t18} forceRedirectToCart={t19} />;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = Number(course.id);
  let t22;
  if ($[40] !== t21) {
    t22 = <WishlistButton courseId={t21} />;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t20 || $[43] !== t22) {
    t23 = <div className={styles["horizontal-buttons"]}>{t20}{t22}</div>;
    $[42] = t20;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== isConsumerSubscriptionPaused || $[46] !== isMobile || $[47] !== removeBuyNow) {
    t24 = (!removeBuyNow || !isMobile) && !isConsumerSubscriptionPaused ? <ClpPrimaryCtaButton udStyle="secondary" /> : null;
    $[45] = isConsumerSubscriptionPaused;
    $[46] = isMobile;
    $[47] = removeBuyNow;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t23 || $[50] !== t24) {
    t25 = <div className={t14}>{t23}{t24}</div>;
    $[49] = t23;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t13 || $[53] !== t25) {
    t26 = <>{t13}{t25}</>;
    $[52] = t13;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const content = t26;
  const t27 = removeSubscriptionOptionEnabled || !isPersonalPlanPrimaryPurchaseOptions;
  let t28;
  if ($[55] !== content || $[56] !== handleExpand || $[57] !== removeSubscriptionOptionEnabled || $[58] !== showRadio || $[59] !== t27 || $[60] !== title) {
    t28 = <CollapsiblePurchaseSection showRadio={showRadio} disableToggle={removeSubscriptionOptionEnabled} title={title} expanded={t27} onExpand={handleExpand}>{content}</CollapsiblePurchaseSection>;
    $[55] = content;
    $[56] = handleExpand;
    $[57] = removeSubscriptionOptionEnabled;
    $[58] = showRadio;
    $[59] = t27;
    $[60] = title;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  return t28;
};
export const CollapsibleBuyBox = React.memo(CollapsibleBuyBoxInner);